import type { Popover } from 'radix-ui';

type PopoverContentProps = React.ComponentPropsWithoutRef<typeof Popover.Content>;
import { PopupButton, PopupContent, Popup, usePopup } from '@dx-ui/osc-popup-v2';
import { ControlClose } from '@dx-ui/osc-controls';
import { useTranslation } from 'next-i18next';
import { useRef } from 'react';
import { Icon } from '@dx-ui/osc-icon';
import cx from 'classnames';

export type InfoPopup = {
  align?: PopoverContentProps['align'];
  isSolidInfoButton?: boolean;
  label?: string;
  testId?: string;
  onClick?: () => void;
  side?: PopoverContentProps['side'];
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export const InfoPopupContent: React.FC<React.PropsWithChildren<InfoPopup>> = ({
  align,
  children,
  testId,
  isSolidInfoButton,
  label: labelProp,
  onClick,
  side,
}) => {
  const { closePopup } = usePopup();
  const { t } = useTranslation('osc-info-popup');
  const infoIconRef = useRef<HTMLButtonElement>(null);
  const label = labelProp || t('moreInfo');

  return (
    <>
      <PopupButton
        className={cx('group', {
          'btn--base btn--brand btn--flat': !isSolidInfoButton,
          'btn--base btn--brand btn--solid': isSolidInfoButton,
        })}
        data-testid={`popup-button${testId ? `-${testId}` : ''}`}
        ref={infoIconRef}
        onClick={onClick}
        aria-label={label}
      >
        <Icon
          name="info-circle"
          variant="regular"
          size="md"
          className={cx('size-6', {
            'group-focus-within:hidden group-hover:hidden': !isSolidInfoButton,
            'hidden group-focus-within:block group-hover:block': isSolidInfoButton,
          })}
        />
        <Icon
          name="info-circle"
          variant="solid"
          size="md"
          className={cx('size-6', {
            'group-focus-within:block group-hover:block hidden': !isSolidInfoButton,
            'group-focus-within:hidden group-hover:hidden': isSolidInfoButton,
          })}
        />
      </PopupButton>
      <PopupContent
        className="w-fit max-w-prose"
        data-testid={`popup-content${testId ? `-${testId}` : ''}`}
        align={align}
        side={side}
      >
        <div className="flex justify-end">
          <ControlClose label={t('close', { label })} onClick={closePopup} size="sm" />
        </div>
        <div className="text--sm mt-1 text-start font-normal">{children}</div>
      </PopupContent>
    </>
  );
};

export const InfoPopup: React.FC<React.PropsWithChildren<InfoPopup>> = ({
  align,
  testId,
  children,
  isSolidInfoButton,
  label,
  onClick,
  side,
  ...rest
}) => (
  <Popup {...rest}>
    <InfoPopupContent
      align={align}
      label={label}
      testId={testId}
      onClick={onClick}
      side={side}
      isSolidInfoButton={isSolidInfoButton}
    >
      {children}
    </InfoPopupContent>
  </Popup>
);

export default InfoPopup;
